"use client";

import * as React from "react";
import { Download, ExternalLink, ArrowRight } from "lucide-react";
import { Modal, Button } from "@/components/ui";
import type { AdOverlayPublicDTO } from "@/types/ad-overlay";

export type OverlayAction = { type: "download" | "view"; href: string };

/** Gửi đo lường kiểu beacon (không chờ kết quả, không làm hỏng luồng). */
function track(id: string, type: "impression" | "continue") {
  try {
    fetch(`/api/ad-overlays/${id}/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ type }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* bỏ qua */
  }
}

/**
 * Lớp phủ quảng cáo. Khi `action` có giá trị → chế độ thật (đếm ngược + đo lường +
 * đi tới link đích). Khi `action` = null → chế độ xem thử cho admin (nút chỉ đóng).
 */
export function AdOverlayDialog({
  overlay,
  action,
  open,
  onClose,
  previewMode = false,
}: {
  overlay: AdOverlayPublicDTO;
  action?: OverlayAction | null;
  open: boolean;
  onClose: () => void;
  previewMode?: boolean;
}) {
  const [remaining, setRemaining] = React.useState(overlay.countdownSeconds);
  const impressedRef = React.useRef(false);

  // Bắt đầu đếm ngược + ghi impression mỗi khi mở.
  React.useEffect(() => {
    if (!open) return;
    setRemaining(overlay.countdownSeconds);
    if (!previewMode && !impressedRef.current) {
      impressedRef.current = true;
      track(overlay.id, "impression");
    }
    if (overlay.countdownSeconds <= 0) return;
    const timer = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(timer);
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [open, overlay.id, overlay.countdownSeconds, previewMode]);

  // Reset cờ impression khi đóng để lần mở sau đếm lại.
  React.useEffect(() => {
    if (!open) impressedRef.current = false;
  }, [open]);

  const ready = remaining <= 0;

  function proceed() {
    if (!previewMode) track(overlay.id, "continue");
    if (action) {
      if (action.type === "download") window.location.href = action.href;
      else window.open(action.href, "_blank", "noopener,noreferrer");
    }
    onClose();
  }

  const defaultContinue = action?.type === "download" ? "Tiếp tục tải xuống" : "Tiếp tục tới trang";
  const continueLabel = overlay.continueText?.trim() || defaultContinue;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title={overlay.heading || "Thông báo"}
    >
      <div className="space-y-4">
        {overlay.imageUrl && (
          <div className="flex min-h-[160px] items-center justify-center overflow-hidden rounded-xl border border-border bg-subtle">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={overlay.imageUrl}
              alt={overlay.heading || "Quảng cáo"}
              className="max-h-[46vh] w-full object-contain"
            />
          </div>
        )}

        {overlay.body && (
          <p className="whitespace-pre-line text-body-sm leading-relaxed text-foreground-secondary">
            {overlay.body}
          </p>
        )}

        {overlay.ctaText && overlay.ctaUrl && (
          <a
            href={overlay.ctaUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-500 px-4 py-3 text-body-sm font-bold text-white transition-opacity hover:opacity-90"
          >
            {overlay.ctaText}
            <ExternalLink className="size-4" aria-hidden="true" />
          </a>
        )}

        <div className="border-t border-border pt-4">
          <Button block size="lg" onClick={proceed} disabled={!ready}>
            {ready ? (
              <>
                {action?.type === "view" ? (
                  <ExternalLink className="size-4" aria-hidden="true" />
                ) : action ? (
                  <Download className="size-4" aria-hidden="true" />
                ) : (
                  <ArrowRight className="size-4" aria-hidden="true" />
                )}
                {previewMode ? "Đóng xem thử" : continueLabel}
              </>
            ) : (
              `Vui lòng chờ ${remaining}s…`
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
